import { useState, useEffect, useMemo, useRef } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { Typography } from 'antd';

import isEmpty from 'lodash/isEmpty';

import { dialogFunction, CustomNotification } from 'common/components';
import { QaSpecHelpContentGridLabel } from 'pages/qa-spec/components/shared/QaSpecHelpContent';

import { useQaSpecGrid } from './useQaSpecGrid';
import useCheckSnapshotForRetailer from './useCheckSnapshotForRetailer';
import { useGetQaSpecRequiredFieldsInQaSpecFullView } from './useGetQaSpecRequiredFields';
import { useGetPhysicalChemicalQuery } from 'pages/qa-spec/query';

import { sleep } from 'utils/delay';
import { apiHandler } from 'utils/api';
import sortByKey from 'utils/sortByKey';
import { getAllRows } from 'pages/qa-spec/utils';
import { addErrorsToRow } from 'pages/qa-spec/utils';
import { checkSectionRequired } from 'pages/qa-spec/utils';
import { createTextEditorRenderer } from 'pages/qa-spec/utils/gridColumnUtils';
import {
  getProductSpecRequiredFields,
  validateAllRows,
  validatePhysicalAndChemicalCharacteristicGrid,
  validateRequiredFieldProductSpec,
} from 'pages/qa-spec/components/qa-prod-spec/utils';

import { queryQaEvalFormKey } from 'pages/new-evaluation-form/hooks/useGetEvaluationForm';

import {
  transformBooleanToStringQaSpecValue,
  transformStringToBooleanQaSpecValue,
} from 'pages/qa-spec/utils/transformQaBooleanValue';

import * as qaSpecServices from 'services/qaSpec';

import { useIntl } from 'react-intl';
import Messages from 'i18n/messages/qa-spec';

export const usePhyChe = ({ productId, enumOptions, isGridEditing }) => {
  const intl = useIntl();
  const gridInst = useRef();

  const queryClient = useQueryClient();

  const isToggleSnapshot = useCheckSnapshotForRetailer();

  const [saveLoading, setSaveLoading] = useState(false);

  const { data: requiredFields } = useGetQaSpecRequiredFieldsInQaSpecFullView({
    productId,
  });

  const isToggleRequiredFields = !isEmpty(requiredFields);

  const physicalAndChemicalRequiredFields = getProductSpecRequiredFields({
    gridInst,
    requiredFields,
    requiredFieldPath:
      'qaSpecification.qaSpecProductSpecification.qaSpecProductSpecsPhysicalAndChemicalCharacteristics'.toLowerCase(),
  });

  const isSectionRequired = checkSectionRequired(requiredFields, 'physical');

  const resetGridCallback = () => {
    if (isToggleRequiredFields) {
      validateAllRows({
        gridInst,
        requiredFields: physicalAndChemicalRequiredFields,
      });
    }
  };
  const { handleRefetchPhysicalChemical } = useGetPhysicalChemicalQuery({
    productId,
  });

  const { physicalChemicalData, isLoading, isFetching } =
    useGetPhysicalChemicalQuery({
      productId,
      enabled: !!productId,
    });

  const {
    rowData,
    addGridRow,
    selectedRow,
    selectGridRow,
    resetGridData,
    deleteSelectedGridRow,
  } = useQaSpecGrid({
    gridInst,
    isGridEditing,
    gridData: physicalChemicalData?.physicalAndChemicalCharacteristics,
    resetGridCallback,
  });

  const fetchDataLoading = isLoading && isFetching;

  const columnDefs = useMemo(() => {
    const statusField = {
      minWidth: 50,
      field: 'error',
      displayName: ' ',
      cellRenderer: 'StatusRender',
      type: 'nonEditableColumn',
    };

    let cols = [
      {
        allowFilter: true,
        allowSort: true,
        dataType: 'string',
        headerName: 'Parameter',
        field: 'parameter',
        width: 200,
        flex: null,
        cellEditorSelector: () => {
          return {
            popup: true,
            component: 'SelectionFreeText',
            params: {
              values: sortByKey(enumOptions.parameter, 'enumDisplayName').map(
                (item) => ({
                  value: item.enumDisplayName,
                  displayName: item.enumDisplayName,
                })
              ),
              maxLength: 150,
            },
          };
        },
        cellRenderer: isToggleSnapshot ? 'TextRenderSnapshot' : 'TextRender',
        headerComponent: QaSpecHelpContentGridLabel,
        headerComponentParams: {
          fieldPath:
            'qaSpecification.productSpecification.physicalAndChemicalCharacteristics.parameter',
        },
      },
      {
        allowFilter: true,
        allowSort: true,
        dataType: 'string',
        headerName: 'Method',
        field: 'method',
        width: 200,
        cellEditorSelector: createTextEditorRenderer(),
        cellRenderer: isToggleSnapshot ? 'TextRenderSnapshot' : 'TextRender',
        headerComponent: QaSpecHelpContentGridLabel,
        headerComponentParams: {
          fieldPath:
            'qaSpecification.productSpecification.physicalAndChemicalCharacteristics.method',
        },
      },
      {
        allowFilter: true,
        allowSort: true,
        dataType: 'string',
        headerName: 'Value (Food and Non-Food products)',
        field: 'value',
        width: 350,
        flex: null,
        cellEditorSelector: createTextEditorRenderer(),
        cellRenderer: isToggleSnapshot ? 'TextRenderSnapshot' : 'TextRender',
        headerComponent: QaSpecHelpContentGridLabel,
        headerComponentParams: {
          fieldPath:
            'qaSpecification.productSpecification.physicalAndChemicalCharacteristics.value',
        },
      },
      {
        allowFilter: true,
        allowSort: true,
        dataType: 'string',
        headerName: 'Specification/Description/Target',
        field: 'specification',
        cellEditorSelector: createTextEditorRenderer(),
        cellRenderer: isToggleSnapshot ? 'TextRenderSnapshot' : 'TextRender',
        width: 280,
        flex: null,
        headerComponent: QaSpecHelpContentGridLabel,
        headerComponentParams: {
          fieldPath:
            'qaSpecification.productSpecification.physicalAndChemicalCharacteristics.specification',
        },
      },
      {
        allowFilter: true,
        allowSort: true,
        dataType: 'string',
        headerName: 'Tolerance',
        field: 'tolerance',
        cellEditorSelector: createTextEditorRenderer(),
        cellRenderer: isToggleSnapshot ? 'TextRenderSnapshot' : 'TextRender',
        width: 200,
        headerComponent: QaSpecHelpContentGridLabel,
        headerComponentParams: {
          fieldPath:
            'qaSpecification.productSpecification.physicalAndChemicalCharacteristics.tolerance',
        },
      },
      {
        allowFilter: true,
        allowSort: true,
        dataType: 'string',
        headerName: 'Reject/Fail Criteria',
        field: 'rejectOrFailCriteria',
        width: 200,
        flex: null,
        cellEditorSelector: createTextEditorRenderer(),
        cellRenderer: isToggleSnapshot ? 'TextRenderSnapshot' : 'TextRender',
        headerComponent: QaSpecHelpContentGridLabel,
        headerComponentParams: {
          fieldPath:
            'qaSpecification.productSpecification.physicalAndChemicalCharacteristics.rejectOrFailCriteria',
        },
      },
      {
        allowFilter: true,
        allowSort: true,
        dataType: 'string',
        headerName: 'Ref. Standard',
       minWidth: 186,
        field: 'referenceStandard',
        cellEditorSelector: createTextEditorRenderer(),
        cellRenderer: isToggleSnapshot ? 'TextRenderSnapshot' : 'TextRender',
        headerComponent: QaSpecHelpContentGridLabel,
        headerComponentParams: {
          fieldPath:
            'qaSpecification.productSpecification.physicalAndChemicalCharacteristics.referenceStandard',
        },
      },
      {
        allowFilter: true,
        allowSort: true,
        dataType: 'string',
        headerName: 'Ref. Method',
        minWidth: 186,
        field: 'referenceMethod',
        cellEditorSelector: createTextEditorRenderer(),
        cellRenderer: isToggleSnapshot ? 'TextRenderSnapshot' : 'TextRender',
        headerComponent: QaSpecHelpContentGridLabel,
        headerComponentParams: {
          fieldPath:
            'qaSpecification.productSpecification.physicalAndChemicalCharacteristics.referenceMethod',
        },
      },
      {
        allowFilter: true,
        allowSort: true,
        dataType: 'string',
        headerName: 'Test Source',
        field: 'test',
        cellEditorSelector: () => {
          return {
            component: 'SelectionEditor',
            params: {
              values: [
                { value: 'Internal', displayName: 'Internal' },
                { value: 'External', displayName: 'External' },
              ],
              mapValue: (value) => {
                if (value === 'null' || value === undefined) {
                  return null;
                } else {
                  return value;
                }
              },
              popup: true,
            },
          };
        },
        cellRenderer: isToggleSnapshot ? 'TextRenderSnapshot' : 'TextRender',
        headerComponent: QaSpecHelpContentGridLabel,
        headerComponentParams: {
          fieldPath:
            'qaSpecification.productSpecification.physicalAndChemicalCharacteristics.test',
        },
      },
      {
        allowFilter: true,
        allowSort: true,
        dataType: 'string',
        headerName: 'Frequency',
        field: 'frequency',
        cellEditorSelector: createTextEditorRenderer(),
        cellRenderer: isToggleSnapshot ? 'TextRenderSnapshot' : 'TextRender',
        headerComponent: QaSpecHelpContentGridLabel,
        headerComponentParams: {
          fieldPath:
            'qaSpecification.productSpecification.physicalAndChemicalCharacteristics.frequency',
        },
      },
      {
        allowFilter: true,
        allowSort: true,
        dataType: 'string',
        headerName: 'Release Criteria',
        minWidth: 186,
        field: 'releaseCriteria',
        cellEditorSelector: () => {
          return {
            component: 'SelectionEditor',
            params: {
              values: [
                { value: 'Yes', displayName: 'Yes' },
                { value: 'No', displayName: 'No' },
                { value: 'Not Applicable', displayName: 'Not Applicable' },
              ],
              mapValue: (value) => {
                if (value === 'null' || value === undefined) {
                  return null;
                } else {
                  return value;
                }
              },
              popup: true,
            },
          };
        },
        cellRenderer: isToggleSnapshot ? 'TextRenderSnapshot' : 'TextRender',
        headerComponent: QaSpecHelpContentGridLabel,
        headerComponentParams: {
          fieldPath:
            'qaSpecification.productSpecification.physicalAndChemicalCharacteristics.releaseCriteria',
        },
      },
    ];

    cols = isToggleRequiredFields ? [statusField, ...cols] : cols;

    return cols.map((fieldItem) => {
      const DEFAULT_WIDTH = 150;

      let initColSetting = {
        type: 'editableColumn',
      };

      return {
        minWidth: DEFAULT_WIDTH,
        ...initColSetting,
        ...fieldItem,
      };
    });
  }, [enumOptions, isToggleSnapshot]);

  const cancelEdit = () => {
    resetGridData();
  };

  const addNewRow = () => {
    let newRowData = {
      method: null,
      parameter: null,
      specification: null,
      tolerance: null,
      frequency: null,
      releaseCriteria: null,
      value: null,
      referenceStandard: null,
      referenceMethod: null,
      test: null,
    };

    if (isToggleRequiredFields) {
      const errorRequiredFields = physicalAndChemicalRequiredFields?.map(
        (field) => field?.errorMessage
      );
      newRowData = addErrorsToRow(newRowData, errorRequiredFields);
      addGridRow(newRowData);
      return;
    }

    addGridRow(newRowData);
  };

  const saveData = async (toggleEditMode, analysisNotExecuted) => {
    gridInst.current.api.stopEditing();

    const GRID_VALIDATION_DELAY = 700;
    await sleep(GRID_VALIDATION_DELAY);

    // const isGridHaveError = validatePhysicalAndChemicalCharacteristicGrid({
    //   grid: gridInst,
    // }).checkIfGridHaveError();

    // if (isGridHaveError) {
    //   CustomNotification.error(
    //     `Please resolve the Physical and Chemical Characteristics grid's errors.`
    //   );
    //   return;
    // }
    
    const allRowData = getAllRows(gridInst);

    if (
      isToggleRequiredFields &&
      physicalAndChemicalRequiredFields?.length > 0 &&
      allRowData?.length <= 0
    ) {
      CustomNotification.error('Please add at least one row data to save');
      return;
    }

    //* clear selected row
    selectGridRow(null);

    setSaveLoading(true);

    const params = {
      productId,
      physicalAndChemicalCharacteristics: allRowData,
      analysisNotExecuted:
        transformStringToBooleanQaSpecValue(analysisNotExecuted),
    };

    const successMessage = intl.formatMessage(
      Messages.productSpecSavePhyCheSuccess
    );
    const errorMessage = intl.formatMessage(
      Messages.productSpecSavePhyCheError
    );

    apiHandler({
      service: qaSpecServices.saveQaSpecPhyCheCharacteristic,
      params,
      successMessage,
      errorMessage,
      successCallback: saveSuccessCallback(toggleEditMode),
      onFinally: saveFinally,
    });
  };

  const saveSuccessCallback = (toggleEditMode) => () => {
    toggleEditMode();

    handleRefetchPhysicalChemical();

    queryClient.invalidateQueries({
      queryKey: queryQaEvalFormKey(productId, 'Food'),
    });
  };

  const saveFinally = () => {
    setSaveLoading(false);
  };

  const deleteData = () => {
    dialogFunction({
      type: 'warn',
      content: 'Are you sure you want to delete the selected item?',
      okText: 'OK',
      cancelText: 'Cancel',
      onOk: onDeleteData,
    });
  };

  const onDeleteData = () => {
    deleteSelectedGridRow();
  };

  const selectRow = () => {
    selectGridRow();
  };

  const handleValidateRequiredFieldForSupplier = ({ event }) => {
    physicalAndChemicalRequiredFields?.forEach((item) => {
      if (item?.fieldName === event.column.colId)
        validateRequiredFieldProductSpec({
          gridInst,
          event,
          requiredFieldError: item.errorMessage,
        });
    });
  };

  const onCellEditingStopped = async (event) => {
    if (isToggleRequiredFields) {
      handleValidateRequiredFieldForSupplier({
        event,
      });
    }
  };

  useEffect(() => {
    const handleValidateAllRowDataForSupplier = () => {
      if (rowData && rowData?.length > 0 && isToggleRequiredFields) {
        validateAllRows({
          gridInst,
          requiredFields: physicalAndChemicalRequiredFields,
        });
      }
    };

    handleValidateAllRowDataForSupplier();
  }, [
    isToggleRequiredFields,
    physicalAndChemicalRequiredFields,
    JSON.stringify(rowData),
  ]);
  const gridTitle = (
    <>
      {isToggleRequiredFields && isSectionRequired && (
        <>
          <Typography.Text type='danger'>*</Typography.Text>{' '}
        </>
      )}
      Physical and Chemical Characteristics
    </>
  );

  return {
    gridInst,
    analysisNotExecuted: transformBooleanToStringQaSpecValue(
      physicalChemicalData?.analysisNotExecuted
    ),
    rowData,
    columnDefs,
    addNewRow,
    saveData,
    cancelEdit,
    deleteData,
    selectedRow,
    selectRow,
    loading: fetchDataLoading || saveLoading,
    onCellEditingStopped,
    isToggleRequiredFields,
    gridTitle,
  };
};
